import StateObserver, { now, user } from 'src/StateObserver';
import {
  startMatch2,
  waitForMatch2Sequence,
} from '../minigame/match2-timestep/match2.sequence';
import { GCInstant as platform } from 'src/lib/gcinstant';
import { hideLoading, showLoading } from 'src/state/ui';
import { getEquippedPetSpecies } from 'src/replicant/pet/pet.getters';
import { getHomePetCrate } from '../pet/pet.utils';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import * as Tutorial from 'src/replicant/tutorial';
import { inviteFriendUnlockFlow } from 'src/game/social/social.sequence';
import { inGamePopupAfterTutorial } from '../launch/ingamePopup.sequence';
import { CareItemID } from 'src/replicant/care/care.ruleset';
import * as Care from 'src/replicant/care';
import { trackPinchEvent } from 'src/lib/analytics/events';
import { animDuration, getApplication, waitForItPromise } from 'src/lib/utils';
import type Match2Scene from '../scenes/Match2Scene';
import { showAutoDialog } from '../conversation/conversation.utils';
import {
  puzzleCoinsTrackTutorialStart,
  puzzleCoinsTrackStepStart,
  puzzleCoinsTrackStepFinish,
  puzzleCoinsTrackTutorialFinish,
} from './puzzlecoins.tracking';
import * as Social from 'src/replicant/social';
import { startInstallNativeSequence } from '../native/native.sequence';
import { Feature } from 'src/replicant/analytics';
import { captureGenericError } from 'src/lib/sentry';
import type { TutorialStep } from 'src/replicant/tutorial/tutorial.ruleset';
import { playMatch2 } from '../minigame/match2-timestep/match2.play';
import {
  allowStartertPackHomeButton,
  showStarterPackPromoFromTutorial,
} from '../starterpack/starterpack.utils';
import { registerReferralFirstEntry } from '../social/social.utils';
import { isDynamicTestInBucket } from 'src/replicant/getters/ab';
import ab from 'src/replicant/ruleset/ab';
import { TutorialBlockTipView } from './views/TutorialBlockTipView';
import i18n from 'src/lib/i18n/i18n';
import { TutorialMovesTipView } from './views/TutorialMovesTipView';
import { getPet } from '../pet';
import { getLaunchScene } from 'src/lib/stateUtils';
import sounds from 'src/lib/sounds';
import {
  getFirstPuzzleBackgroundAssets,
  isLoadingFTUEActive,
} from 'src/lib/ftue';
import { cardsOpenShareLinkFromEntry } from '../cards/cards.share.entry';
import {
  isStickersEntry,
  openStickersPopup,
} from '../stickers/stickers.sequence';

/**
 * Launch sequence for new tutorial, if suitable.
 */
export async function puzzleCoinsFirstEntrySequence(): Promise<boolean> {
  // Skip if user is not at the first level
  if (user().match2.level !== 1) return false;

  // Check if user has a pet already
  const species = getEquippedPetSpecies(user());

  if (!species && !isLoadingFTUEActive()) {
    // Leave the initial crate created already, if user does not have a pet
    // So the crate will be there imediately after the first puzzle is played
    getHomePetCrate();
  }

  // Auto complete the match2 tutorial step in this feature
  if (!Tutorial.getters.isTutorialStepComplete(user(), 'match2')) {
    StateObserver.invoke.forceCompleteTutorialStep('match2');
    // Track the start of the tutorial, will be finished in the PuzzleCoinsCareTutorial
    // Doing it here so we can track tutorial start only once per user
    puzzleCoinsTrackTutorialStart();
  }

  // Block pet auto dialog during the tutorial
  getPet().conversation.blockAutoDialog(true);

  // Just prevent the starterpack button from showing up during the tutorial
  allowStartertPackHomeButton(false);

  // Start the match2 game.
  await startMatch2({
    onSceneEntered: () => {
      // Dismiss the in-game loading spinner
      if (StateObserver.getState().ui.loading > 0) {
        StateObserver.dispatch(hideLoading());
      }

      // Dismiss the splash screen loading image
      void platform.dismissLoadingScreenAsync({
        launchPath: 'puzzle_coins_first_puzzle',
      });

      if (!species) {
        // Leave the initial crate created already, if user does not have a pet
        // So the crate will be there imediately after the first puzzle is played
        getHomePetCrate();
      }

      // Start loading remaining assets in background once the first puzzle is
      // started. The background chain itself follows the splash dismissal above.
      void getFirstPuzzleBackgroundAssets().load();
      void getApplication().startFirstSceneLoading();
    },
    onPuzzleFinish: () => {
      // Track first puzzle completion, the start was triggered after the first block tip
      puzzleCoinsTrackStepFinish('firstPuzzle');

      // Register the referral first entry only after the first puzzle is completed
      registerReferralFirstEntry();

      // Open the first pet scene that has been skipped when going straight to puzzle
      void puzzleCoinsOpenPetFirstScene();
    },
  });

  return true;
}

/**
 * Runs the in-game Terms of Service (ToS) sequence for the Puzzle Coins feature.
 *
 * @remarks
 * This function is called within the match2 scene before displaying the first block tip. It handles
 * the ToS acceptance process.
 *
 * @async
 *
 * @description
 * The function performs the following steps:
 * 1. Automatically completes the puzzle hats match2 tip
 * 2. Checks if the user is in the tutorial revamp test group and runs an alternative tutorial if so
 * 3. Displays the ToS popup and tracks its acceptance
 * 4. Shows a block tip for puzzle cells
 * 5. Tracks the start of the first puzzle
 *
 * @returns {Promise<void>} A promise that resolves when the ToS and block tip sequence is complete
 *
 * @throws {Error} Potential errors during ToS popup or scene interaction
 */
export async function puzzleCoinsToSAndTip() {
  // Auto complete the puzzle hats match2 tip, as it wil be shown anyway in the block tip step
  void StateObserver.invoke.tipComplete({ id: 'puzzleHats' });

  // Start the block tip step
  puzzleCoinsTrackStepStart('blockTipAndTos');

  // Block tip Step
  const scene = getMatch2Scene();

  /**
   * Block tip step
   */

  // Show tip for the block tutorial
  const blockTutorialTip = new TutorialBlockTipView(
    scene.getGameHighlightView(),
  );

  // Show the block tip
  blockTutorialTip.showTip(
    i18n('tutorial.revamp.cardboardTip'),
    { x: 0, y: -180 },
    { x: 130, y: -40 },
  );

  // Wait for player move to continue
  await scene?.waitForTap();

  // Hide block tip
  await blockTutorialTip.hideTip();

  // Complete the block tip step
  puzzleCoinsTrackStepFinish('blockTipAndTos');

  // Wait a bit for animations to finish
  await waitForItPromise(animDuration);

  // Cleanup view
  blockTutorialTip.removeFromSuperview();

  /**
   * Moves tip step
   */

  // Start move tip step
  puzzleCoinsTrackStepStart('movesTip');

  // Create move tip view on the scene
  const movesTip = new TutorialMovesTipView(scene.getMovesView());

  // Highlight the moves view
  const stopHighlight = scene.highlightMovesView();

  // Show the moves tip, and wait for play button to be pressed
  await movesTip.showTip(
    i18n('tutorial.revamp.movesTip'),
    { x: -126, y: 660 },
    { x: 106, y: 184 },
  );

  // Stop highlighting the moves view
  await stopHighlight();

  // Cleanup view
  movesTip.removeFromSuperview();

  // End move tip step
  puzzleCoinsTrackStepFinish('movesTip');

  // Track the start of the first puzzle after block tip
  puzzleCoinsTrackStepStart('firstPuzzle');
}

/**
 * Check if we should run the post toy care sequence.
 * @param careItemId
 * @returns
 */
export function puzzleCoinsShouldRunPostToyCareSequence(
  careItemId: CareItemID,
) {
  // Only after the second care action is completed
  if (user().care.careActionCount !== 2) return false;

  // Only if the care item is a toy
  const careItem = Care.ruleset.careItems[careItemId];
  const { type } = careItem;
  if (type !== 'toy') return false;

  // Only if player is at level 3, meaning that the tutorial is complete
  if (user().match2.level !== 3) return false;

  // All conditions met, can run the sequence
  return true;
}

/**
 * Play this sequence right after the pet play with the toy, at the end of the tutorial
 * This will show relevant popups that were skipped by the PuzzleCoinsLaunchSequence and post-match2 sequence.
 * Done this way so the care action after puzzle gameplay does not get interrupted by popups.
 */
export async function puzzleCoinsPostToyCareSequence() {
  // Tutorial users bypass the default launch card share block, so handle it
  // here with the other catch-up popups after toy care has finished.
  await cardsOpenShareLinkFromEntry('launch');

  // Show the sticker OA popup after tutorial care.
  if (isStickersEntry()) {
    await openStickersPopup(platform.entryData);
  }

  // SC In-game Popup A/B (0477): post-tutorial OA Add-Friend ask for the
  // still-unsubscribed non-control arms. No-op for control / other users.
  await inGamePopupAfterTutorial();

  // Show the invite challenge, if running
  if (Social.getters.isInviteChallengeRunning(user(), now())) {
    await openPopupPromise('popupInviteChallenge', {});
  }

  // Run the play-on-app sequence
  await startInstallNativeSequence({
    originFeature: Feature.BPOINTS_FROM_PUZZLE._,
  });

  // Run the friends unlock sequence
  await inviteFriendUnlockFlow();

  // Show pet starter pack promo, if available
  await showStarterPackPromoFromTutorial();

  // Unblock pet auto dialog after tutorial completion
  getPet().conversation.blockAutoDialog(false);

  // Play pet auto dialog, if any
  await showAutoDialog();
}

/**
 * Confirm the care item purchase without a confirmation popup.
 * It will trigger the pinch sequence if the user does not have enough resources.
 * @param careItemId
 * @returns
 */
export async function puzzleCoinsConfirmCareItem(careItemId: CareItemID) {
  const canPurchase = canPurchaseCareItem(careItemId);
  if (!canPurchase) {
    trackCareItemPinchEvent(careItemId);
    try {
      await puzzleCoinsPinch();
    } catch (error) {
      captureGenericError('CoinPinchSequenceError', error);
      return { accepted: false };
    }
  }
  return { accepted: true };
}

// Copied from PopupConfirmCareItem.ts
function trackCareItemPinchEvent(careItemId: CareItemID) {
  const careItem = Care.ruleset.careItems[careItemId];
  const type = careItem.type;
  if (type === 'invitation') {
    trackPinchEvent({
      resource: 'bPoints',
      trigger: 'duo',
      item_id: careItemId,
    });
  } else {
    trackPinchEvent({
      resource: 'bPoints',
      trigger: `careAction_${type}`,
      item_id: careItemId,
    });
  }
}

// Copied from PopupConfirmCareItem.ts
function canPurchaseCareItem(careItemId: CareItemID) {
  const careItem = Care.ruleset.careItems[careItemId];
  const balanceAfterPurchase = user().inventory.points - careItem.price;
  return balanceAfterPurchase >= 0;
}

/**
 * Run the pinch sequence for the user to get bPoints from the puzzle minigame.
 */
export async function puzzleCoinsPinch() {
  // Trigger the match2 pre-game sequence in coins pinch mode
  await playMatch2({ coinsPinch: true, origin: 'pinch' });

  // User can choose to play the puzzle again after beating a level, in the post game sequence.
  // This will ensure awaiting all match2 gameplay sessions started from playing again.
  // It is outside the playMatch2() method to avoid unexpected side effects on other features.
  await waitForMatch2Sequence();
}

function getMatch2Scene() {
  const minigame = getApplication().screenManager.screens.minigame;
  return minigame?.currentScene as Match2Scene;
}

/**
 * Fixed tutorial steps that are not completed after user passes level 2. This can happen if user first entry was on control
 * but come back on enabled bucket, without completing the basic tutorial steps.
 * @returns
 */
export async function puzzleCoinsAutoCompleteTutorials() {
  // Only auto complete if user has passed the level 2
  if (user().match2.level <= 2) return;

  // List of tutorial steps related to this feature that should be auto completed
  const stepsToAutoComplete: TutorialStep[] = [
    'match2',
    'puzzleCoinsBasic',
    'puzzleCoinsCare',
    'chatWithPets',
  ];

  let autoCompleted = false;

  // Auto complete the tutorial steps listed
  for (const step of stepsToAutoComplete) {
    if (!Tutorial.getters.isTutorialStepComplete(user(), step)) {
      StateObserver.invoke.forceCompleteTutorialStep(step);
      autoCompleted = true;
    }
  }

  // Fire the TutorialFinish event if steps have been auto completed
  if (autoCompleted) {
    puzzleCoinsTrackTutorialFinish(true);
  }
}

/**
 * Open the first pet scene, moved from PuzzleCoinsLaunchSequence to here
 */
export async function puzzleCoinsOpenPetFirstScene() {
  const scene = getLaunchScene();
  const petController = getPet();

  // Block the entire screen in advance in case there is a save state, to prevent
  // user taking any action before the save state is loaded and gameplay started.
  StateObserver.dispatch(showLoading());

  if (scene === 'bedroom') {
    await petController.moveTo({ scene, location: 'bed' }, 0);
    petController.appearance.setAction('sleep');
  } else {
    await petController.moveTo({ scene, location: 'c' }, 0);
  }

  await platform.dismissLoadingScreenAsync({
    launchPath: 'puzzle_coins_pet_scene',
  });
  // Start bgm once the scene is entered.
  sounds.music.playBackgroundMusic('bgm');

  const { settings } = StateObserver.getState().user;
  sounds.musicMuted = !settings.music;
  sounds.sfxMuted = !settings.sound;

  // Hide the loading showed at the beginning of this action block
  StateObserver.dispatch(hideLoading());
}
